import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { tr } from "../lib/i18n";

const FIELDS = [["title_en", "q_en", "ltr"], ["title_ar", "q_ar", "rtl"], ["keywords_en", "kw_en", "ltr"], ["keywords_ar", "kw_ar", "rtl"]];
const blank = () => ({ title_en: "", title_ar: "", keywords_en: "", keywords_ar: "", answer_en: "", answer_ar: "", active: true });
const COMMON = new Set(["what", "how", "the", "is", "see", "online", "who", "where", "when", "why", "can", "you", "and", "for", "about", "have", "want", "need", "please", "ما", "كيف", "هل", "في", "من", "عن", "شو", "كم", "ماذا", "لماذا", "اين", "أين", "متى"]);
const broad = (s) => s.split(",").map((k) => k.trim()).filter((k) => k && (k.length < 4 || COMMON.has(k.toLowerCase())));

function FaqEditor({ f, n, set }) {
  const up = (k) => (e) => set({ ...f, [k]: e.target.value });
  return (
    <div className="grid sm:grid-cols-2 gap-3 pt-3" data-testid={`faq-editor-${n}`}>
      {FIELDS.map(([k, l, dir]) => (
        <label key={k} className="block text-sm">{tr(`faq.${l /* i18n-keys: q_en,q_ar,kw_en,kw_ar */}`)}
          <input className="input w-full mt-1" dir={dir} value={f[k]} onChange={up(k)} maxLength={k.startsWith("title") ? 120 : 400} data-testid={`faq-${k}-${n}`} />
          {k.startsWith("keywords") && broad(f[k]).length > 0 && (
            <span className="block text-xs text-amber-800 mt-1" data-testid={`faq-${k}-warn-${n}`}>{tr("faq.kw_warn", { p0: broad(f[k]).join(", ") })}</span>
          )}
        </label>
      ))}
      {[["answer_en", "a_en", "ltr"], ["answer_ar", "a_ar", "rtl"]].map(([k, l, dir]) => (
        <label key={k} className="block text-sm">{tr(`faq.${l /* i18n-keys: a_en,a_ar */}`)}
          <textarea className="input w-full mt-1" rows={4} dir={dir} maxLength={900} value={f[k]} onChange={up(k)} data-testid={`faq-${k}-${n}`} />
          <span className="text-xs text-muted-foreground" dir="ltr">{tr("faq.chars", { p0: f[k].length })}</span>
        </label>
      ))}
    </div>
  );
}

function FaqRow({ f, n, last, open, setOpen, set, move, del }) {
  return (
    <li className="p-3" data-testid={`faq-row-${n}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium basis-full sm:basis-auto flex-1 min-w-0 break-words">{n + 1}. {f.title_en || f.title_ar || "—"}
          {!f.active && <span className="ms-2 text-xs px-1.5 bg-amber-100 text-amber-900">{tr("faq.inactive")}</span>}</span>
        <label className="inline-flex items-center gap-1 text-xs"><input type="checkbox" checked={f.active} onChange={(e) => set({ ...f, active: e.target.checked })} data-testid={`faq-active-${n}`} />{tr("faq.active")}</label>
        <button type="button" className="btn-ghost p-1" disabled={!n} onClick={() => move(-1)} aria-label={tr("faq.up")} data-testid={`faq-up-${n}`}><ArrowUp size={14} /></button>
        <button type="button" className="btn-ghost p-1" disabled={last} onClick={() => move(1)} aria-label={tr("faq.down")} data-testid={`faq-down-${n}`}><ArrowDown size={14} /></button>
        <button type="button" className="btn-ghost text-xs" onClick={() => setOpen(open ? null : n)} data-testid={`faq-edit-${n}`}>{open ? tr("faq.done") : tr("faq.edit")}</button>
        <button type="button" className="btn-ghost p-1 text-red-700" onClick={del} aria-label={tr("faq.delete")} data-testid={`faq-delete-${n}`}><Trash2 size={14} /></button>
      </div>
      {open && <FaqEditor f={f} n={n} set={set} />}
    </li>
  );
}

function FaqTester({ items }) {
  const [text, setText] = useState("");
  const [first, setFirst] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    try { const { data } = await api.post("/crm/faq/test", { text, first, items }); setRes(data); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="border border-[var(--divider)] p-3 space-y-2" data-testid="faq-tester">
      <h4 className="font-semibold">{tr("faq.test")}</h4>
      <p className="text-xs text-muted-foreground">{tr("faq.test_hint")}</p>
      <div className="flex flex-wrap gap-2">
        <input className="input flex-1 min-w-[200px]" dir="auto" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && text.trim() && run()} placeholder={tr("faq.test_ph")} data-testid="faq-test-input" />
        <button className="btn-secondary" disabled={!text.trim()} onClick={run} data-testid="faq-test-run">{tr("faq.run")}</button>
      </div>
      <label className="inline-flex items-center gap-2 text-xs"><input type="checkbox" checked={first} onChange={(e) => setFirst(e.target.checked)} data-testid="faq-test-first" />{tr("faq.first")}</label>
      {res && (
        <div className="text-sm space-y-1" data-testid="faq-test-result">
          <div className="text-xs text-muted-foreground" data-testid="faq-test-action">{tr(`faq.a_${res.action /* i18n-keys: menu,answer,handover,nomatch */}`)}</div>
          {res.messages.length ? res.messages.map((m, i) => <div key={i} dir="auto" className="whitespace-pre-wrap p-2 bg-emerald-50 border border-emerald-200">{m}</div>)
            : <div className="text-muted-foreground">{tr("faq.silent")}</div>}
        </div>
      )}
    </div>
  );
}

export function FaqSettingsPanel() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/crm/faq").then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  if (!d) return null;
  const items = d.items;
  const setItems = (x) => setD({ ...d, items: x });
  const setAt = (n) => (f) => setItems(items.map((it, i) => (i === n ? f : it)));
  const move = (n) => (dir) => { const x = [...items]; [x[n], x[n + dir]] = [x[n + dir], x[n]]; setItems(x); setOpen(null); };
  const save = async () => {
    try { const { data } = await api.put("/crm/faq", { enabled: d.enabled, items }); setD(data); setOpen(null); toast.success(tr("crm.saved")); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-3 pt-4 border-t border-[var(--divider)]" data-testid="faq-settings">
      <h3 className="font-semibold">{tr("faq.title")}</h3>
      <p className="text-sm text-muted-foreground">{tr("faq.intro")}</p>
      <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={d.enabled} onChange={(e) => setD({ ...d, enabled: e.target.checked })} data-testid="faq-enabled" />{tr("faq.enabled")}</label>
      <ul className="border border-[var(--divider)] divide-y divide-[var(--divider)]" data-testid="faq-list">
        {items.map((f, n) => <FaqRow key={f.id || `new-${n}`} f={f} n={n} last={n === items.length - 1} open={open === n} setOpen={setOpen} set={setAt(n)} move={move(n)}
          del={() => { setItems(items.filter((_, i) => i !== n)); setOpen(null); }} />)}
        {!items.length && <li className="p-3 text-sm text-muted-foreground" data-testid="faq-empty">{tr("faq.empty")}</li>}
      </ul>
      <div className="flex flex-wrap gap-2">
        <button className="btn-secondary" onClick={() => { setItems([...items, blank()]); setOpen(items.length); }} data-testid="faq-add"><Plus size={14} /> {tr("faq.add")}</button>
        <button className="btn-primary" disabled={!d.crm} onClick={save} data-testid="faq-save">{tr("faq.save")}</button>
      </div>
      <FaqTester items={items} />
    </div>
  );
}
